import { useMemo } from 'react';
import { getContent, useContentContext } from 'src/contexts/ContentContext';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { OnSaleEventTableColumnId } from 'src/navigations/Routes/OnSaleDiscovery/OnSaleDiscoveryView.types';
import { RetailTableColumnId } from 'src/tables/RetailTable/RetailTable.types';
import { EventsTableColumnId } from 'src/utils/columns/events/eventsColumnUtils.types';
import {
  ListingReportTableColumnId,
  ListingTableColumnId,
  ListingTableFlattenedColumnId,
} from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { MarketplacePaymentsTableColumnId } from 'src/utils/columns/marketplacePayments/marketplacePaymentsColumnsUtils.types';
import { PurchasesTableColumnId } from 'src/utils/columns/purchases/purchasesColumnUtils.types';
import {
  RecentSalesTableColumnId,
  SalesReportTableColumnId,
  SalesTableColumnId,
  SalesTableFlattenedColumnId,
  SalesTableV1ColumnId,
} from 'src/utils/columns/sales/salesColumnUtils.types';
import {
  CAMPAIGNS_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
  EVENTS_TABLE_COLUMN_ID_TO_CONTENT_ID,
  FLIGHTS_TABLE_COLUMN_ID_TO_CONTENT_ID,
  LISTING_REPORT_TABLE_COLUMN_ID_TO_CONTENT_ID,
  LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID,
  LISTING_TABLE_FLATTENED_COLUMN_ID_TO_CONTENT_ID,
  ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID,
  PAYMENTS_TABLE_COLUMN_ID_TO_CONTENT_ID,
  PURCHASE_TABLE_COLUMN_ID_TO_CONTENT_ID,
  PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
  RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID,
  RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID,
  SALE_REPORT_TABLE_COLUMN_ID_TO_CONTENT_ID,
  SALE_TABLE_COLUMN_ID_TO_CONTENT_ID,
  SALE_TABLE_FLATTENED_COLUMN_ID_TO_CONTENT_ID,
  SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
} from 'src/utils/constants/contentIdMaps';
import {
  SECTION_TYPE_TO_ACTION_OUTBOX_ENTITY_TYPE,
  SECTION_TYPE_TO_CUSTOM_COLUMN_SETTING,
  SectionType,
} from 'src/utils/types/sectionType';

export const getContentIdForColumn = (id: string, sectionType: SectionType) => {
  return {
    [SectionType.Listings]:
      LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[id as ListingTableColumnId],
    [SectionType.ListingsFlattened]:
      LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[id as ListingTableColumnId] ||
      LISTING_TABLE_FLATTENED_COLUMN_ID_TO_CONTENT_ID[
        id as ListingTableFlattenedColumnId
      ],
    [SectionType.InventorySideTable]:
      LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[id as ListingTableColumnId],
    [SectionType.Sales]: {
      ...SALE_TABLE_COLUMN_ID_TO_CONTENT_ID,
      ...SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
    }[id as SalesTableColumnId],
    [SectionType.SalesFlattened]:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId] ||
      SALE_TABLE_FLATTENED_COLUMN_ID_TO_CONTENT_ID[
        id as SalesTableFlattenedColumnId
      ],
    [SectionType.SalesSideTable]:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableV1ColumnId],
    [SectionType.Events]:
      EVENTS_TABLE_COLUMN_ID_TO_CONTENT_ID[id as EventsTableColumnId],
    [SectionType.SalesReport]:
      SALE_REPORT_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesReportTableColumnId],
    [SectionType.Purchases]: {
      ...PURCHASE_TABLE_COLUMN_ID_TO_CONTENT_ID,
      ...PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
    }[id as PurchasesTableColumnId],
    [SectionType.PurchaseEvent]: {
      ...PURCHASE_TABLE_COLUMN_ID_TO_CONTENT_ID,
      ...PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
    }[id as PurchasesTableColumnId],
    [SectionType.PurchaseSideTable]: {
      ...PURCHASE_TABLE_COLUMN_ID_TO_CONTENT_ID,
      ...PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
    }[id as PurchasesTableColumnId],
    [SectionType.ListingsReport]:
      LISTING_REPORT_TABLE_COLUMN_ID_TO_CONTENT_ID[
        id as ListingReportTableColumnId
      ],
    [SectionType.MarketplacePaymentsTable]:
      PAYMENTS_TABLE_COLUMN_ID_TO_CONTENT_ID[
        id as MarketplacePaymentsTableColumnId
      ],
    [SectionType.OnSaleEvents]:
      ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[id as OnSaleEventTableColumnId],
    [SectionType.RecentSalesTable]: {
      ...RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID,
      ...SALE_TABLE_COLUMN_ID_TO_CONTENT_ID,
      ...SALE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
      ...SALE_TABLE_FLATTENED_COLUMN_ID_TO_CONTENT_ID,
    }[
      id as
        | RecentSalesTableColumnId
        | SalesTableColumnId
        | SalesTableFlattenedColumnId
    ], // TODO: Remove this when we have a better way to handle this
    [SectionType.Retail]:
      RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID[id as RetailTableColumnId],
    [SectionType.CampaignsTable]:
      CAMPAIGNS_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[id as never],
    [SectionType.FlightsTable]:
      FLIGHTS_TABLE_COLUMN_ID_TO_CONTENT_ID[id as never],
  }[sectionType];
};

export const isCustomColumn = (id: string, sectionType: SectionType) => {
  return getContentIdForColumn(id, sectionType) == null;
};

export const useColumnDisplayName = ({
  columnIds,
  sectionType,
}: {
  columnIds: string[];
  sectionType: SectionType;
}) => {
  const contentContext = useContentContext();

  const { isTag } = useTagsForEntityType(
    SECTION_TYPE_TO_ACTION_OUTBOX_ENTITY_TYPE[sectionType],
    true
  );

  const { value: customListingColumns = [] } = useServerUserSetting<
    CustomListingColumn[]
  >({
    id: SECTION_TYPE_TO_CUSTOM_COLUMN_SETTING[sectionType],
  });

  const displayNamesWithContentId: (string | undefined)[] = useMemo(
    () =>
      columnIds.map((id) => {
        if (isTag(id)) {
          return id;
        } else if (isCustomColumn(id, sectionType)) {
          return customListingColumns.find((c) => c.id === id)?.name ?? id;
        } else {
          return getContentIdForColumn(id, sectionType);
        }
      }),
    [columnIds, customListingColumns, isTag, sectionType]
  );

  const columnIdDisplayNameMapping: Record<string, string> = useMemo(
    () =>
      columnIds.reduce((access, id) => {
        if (isTag(id)) {
          return {
            ...access,
            [id]: id,
          };
        } else if (isCustomColumn(id, sectionType)) {
          const customColumn = customListingColumns.find((c) => c.id === id);
          return {
            ...access,
            [id]: customColumn?.name ?? id,
          };
        } else {
          const contentId = getContentIdForColumn(id, sectionType);
          return {
            ...access,
            [id]: getContent(contentId, contentContext),
          };
        }
      }, {}),
    [columnIds, customListingColumns, contentContext, isTag, sectionType]
  );

  const displayNames: string[] = useMemo(() => {
    return displayNamesWithContentId.map((name) =>
      getContent(name, contentContext)
    );
  }, [contentContext, displayNamesWithContentId]);

  return { displayNames, columnIdDisplayNameMapping };
};
